/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Viewer from 'react-viewer';
import { IFileModel } from '../../../service/model/ISourceDocumentModel';
import { IFileGuiModel } from '../../../model/ISourceDocumentGuiModel';
import SourceDocumentBusinessLogic from '../../../businessLogic/SourceDocumentBusinessLogic';

interface IProps {
    showFile: boolean,
    sourceDocumentId?: number,
    file: IFileModel | IFileGuiModel | undefined,
    showFileToggle?: () => void,
}

interface IViewerProps {
    file: IFileModel | IFileGuiModel | undefined,
    loader: Boolean,
    showFile: boolean,
    showFileToggle?: () => void,
    filePages: { src: string, alt: string }[],
}

const fileContainerStyle = css({
    height: "100%", width: "100%"
})


const ViewerElement = (props: IViewerProps): JSX.Element => {

    const { t, i18n } = useTranslation();
    function voucherContainerTypeGuard(container: HTMLElement | null | undefined) {
        if (container) {
            const el: HTMLElement = container
            return el;
        }
        else
            return undefined;
    }

    if (props.file) {

        if (props.file.isPreviewReady === true) {
            if (props.file.pages <= 0) {
                return <Box width="100%" display="flex" flexDirection="column">
                    <LinearProgress color="error" />
                    <Typography variant="button" alignSelf="center">{t("basic.fileMissingPages")}</Typography>
                </Box>
            }
            else if (props.loader) {
                return <Box width="100%" display="flex" flexDirection="column">
                    <LinearProgress color="success" />
                    <Typography variant="button" alignSelf="center">{t("basic.loading")}</Typography>
                </Box>
            }
            else {
                return <Viewer zoomSpeed={0.2} noImgDetails disableKeyboardSupport
                    container={voucherContainerTypeGuard(document.getElementById("voucherImageViewMain"))}
                    visible={props.showFile}
                    onClose={props.showFileToggle ? props.showFileToggle : undefined}
                    images={props.filePages.length > 0 ? props.filePages : [{ src: "", alt: "" }]} noClose={props.showFileToggle ? false : true} />
            }
        } else {
            return <Box width="100%" display="flex" flexDirection="column">
                <LinearProgress color="warning" />
                <Typography variant="button" alignSelf="center">{t("basic.fileNotReady")}</Typography>
            </Box>
        }

    } else {
        return <Typography>{t("basic.noFiles")}</Typography>
    }
}

const FileViewer = (props: IProps): JSX.Element => {

    const { organizationId } = useParams();

    const [filePages, setFilePages] = useState<{ src: string, alt: string }[]>([]);

    const [loader, setLoader] = useState(false);

    useEffect(() => {

        let isMounted = true;

        let temp: { src: string, alt: string }[] = [];
        let tempPages;

        const logic = new SourceDocumentBusinessLogic();

        const getFilePage = async (fileId: number, page: number) : Promise<string> => {

            const result = await logic.getSourceDocumentsFilePage(
                organizationId ? organizationId : "",
                fileId,
                page,
                props.sourceDocumentId ? props.sourceDocumentId + "" : "");

            if (result.hasErrors) {
                return ""
            } else {
                if (result.value) {
                    return URL.createObjectURL(result.value);
                } else {
                    return ""
                }
            }
        }
        const addFile = async () => {
            setLoader(true);
            if (props.file?.isPreviewReady === true && props.file?.pages)
                tempPages = props.file.pages;
            else
                tempPages = 0;
            for (let i = 0; i < tempPages; i++) {
                if (props.file) {
                    let url = await getFilePage(props.file.id, i);
                    temp.push({ src: url, alt: "" });
                }
            }
            if (isMounted) {
                setFilePages(temp);
            }
            setLoader(false);
        }
        
        addFile();

        return () => { isMounted = false };

    }, [organizationId, props.file, props.sourceDocumentId])

    return <div css={fileContainerStyle} id="voucherImageViewMain">
        <ViewerElement
            file={props.file}
            loader={loader}
            showFile={props.showFile}
            showFileToggle={props.showFileToggle}
            filePages={filePages} />
    </div>

    
}

export default memo(FileViewer);